/**
 * Status colors as tones, on the shared semantic tokens so they follow the
 * theme. Red is for real failures only; a weak or lapsed state is attention.
 */
export type StatusTone =
  | "success"
  | "primary"
  | "upcoming"
  | "attention"
  | "destructive"
  | "neutral";

/** A tinted pill: the tone's text on a 15% wash of it. */
export const STATUS_PILL: Record<StatusTone, string> = {
  success: "bg-success/15 text-success",
  primary: "bg-primary/15 text-primary",
  upcoming: "bg-info/15 text-info",
  attention: "bg-warning/15 text-warning",
  destructive: "bg-danger/15 text-danger",
  neutral: "bg-foreground/[0.08] text-foreground/80",
};

/** A solid mark (a list's status dot), where a wash would be too faint to see. */
export const STATUS_DOT: Record<StatusTone, string> = {
  success: "bg-success",
  primary: "bg-primary",
  upcoming: "bg-info",
  attention: "bg-warning",
  destructive: "bg-danger",
  neutral: "bg-foreground/50",
};

export const STATUS_TEXT: Record<StatusTone, string> = {
  success: "text-success",
  primary: "text-primary",
  upcoming: "text-info",
  attention: "text-warning",
  destructive: "text-danger",
  neutral: "text-foreground/80",
};

/** A member's membership status (getMembershipStatus's output) as a tone. */
export const membershipTone = (status?: string | null): StatusTone => {
  const value = status?.toLowerCase?.() ?? "";
  if (value === "paid") return "success";
  if (value === "trialing" || value === "canceling") return "primary";
  if (value === "trialing expired") return "attention";
  if (value === "canceled") return "destructive";
  return "neutral";
};

/** getMemberHealth's value as a tone: recent workouts are success, lapsing is attention. */
export const healthTone = (health?: string | null): StatusTone => {
  const value = health?.toUpperCase?.();
  if (value === "HEALTHY" || value === "ACTIVE") return "success";
  if (value === "SLIPPING" || value === "PULSE") return "attention";
  return "neutral";
};
